import type { UseOrgSettingsReturn } from "@/hooks/useOrgSettings";

/**
 * Build a synthetic OrgSettingsContext value for the public storefront.
 *
 * The storefront cannot read `organization_settings` as `anon`, so this
 * derives the bare minimum (currency code + number locale) from values
 * surfaced by the `get_org_by_host` RPC and falls back to safe defaults
 * for everything else. Date / timezone / fiscal-year / accent fields
 * are only surfaced inside the BMS UI and are not used by
 * `StorefrontPreview`, so default values are acceptable.
 */
export function buildStorefrontOrgSettingsValue(input: {
  currencyCode: string | null | undefined;
  numberLocale: string | null | undefined;
}): UseOrgSettingsReturn {
  const currencyCode = (input.currencyCode || "USD").toUpperCase();
  const numberLocale = input.numberLocale || currencyCodeToLocale(currencyCode);
  const currencySymbol = guessCurrencySymbol(currencyCode, numberLocale);

  const fmtCurrencyFormatter = new Intl.NumberFormat(numberLocale, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const fmtCurrencyCompactFormatter = new Intl.NumberFormat(numberLocale, {
    style: "currency",
    currency: currencyCode,
    notation: "compact",
    maximumFractionDigits: 1,
  });
  const dateFormatter = new Intl.DateTimeFormat(numberLocale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

  return {
    currencyCode,
    currencySymbol,
    currencyLabel: `${currencyCode} (${currencySymbol})`,
    dateFormat: "MMM DD, YYYY",
    numberFormat: "1,234.56",
    numberLocale,
    dateLocale: numberLocale,
    timezone: "UTC",
    timezoneIana: "UTC",
    language: "English (US)",
    compactView: false,
    accentColor: "Blue",
    animationLevel: "Fluent",
    fiscalYearStartMonth: 1,
    fiscalYearStartDay: 1,
    isLoading: false,
    fmtCurrency: (amount) => (amount == null ? "-" : fmtCurrencyFormatter.format(amount)),
    fmtCurrencyCompact: (amount) =>
      amount == null ? "-" : fmtCurrencyCompactFormatter.format(amount),
    fmtDate: (date) => {
      if (!date) return "-";
      try {
        const d = typeof date === "string" ? new Date(date) : date;
        if (isNaN(d.getTime())) return "-";
        return dateFormatter.format(d);
      } catch {
        return "-";
      }
    },
    fmtNumber: (value, decimals) => {
      if (value == null) return "-";
      const opts: Intl.NumberFormatOptions = {};
      if (decimals !== undefined) {
        opts.minimumFractionDigits = decimals;
        opts.maximumFractionDigits = decimals;
      }
      return new Intl.NumberFormat(numberLocale, opts).format(value);
    },
  };
}

function currencyCodeToLocale(code: string): string {
  const map: Record<string, string> = {
    USD: "en-US", EUR: "de-DE", GBP: "en-GB", INR: "en-IN",
    JPY: "ja-JP", CAD: "en-CA", AUD: "en-AU", CHF: "fr-CH",
    CNY: "zh-CN", AED: "ar-AE", SAR: "ar-SA",
  };
  return map[code] ?? "en-US";
}

function guessCurrencySymbol(code: string, locale: string): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === "currency")?.value;
    if (sym) return sym;
  } catch {
    /* fall through */
  }
  return code;
}
